import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { facilityAPI, staffingPlanAPI } from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ArrowLeft, LogOut, Sparkles, Loader2, Building2,
  AlertCircle, ChevronRight, Clock
} from 'lucide-react';

const inp = "w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-gray-50";
const sel = inp;

const URGENCY_OPTIONS = [
  { value: 'LOW',      label: 'Low',      desc: 'Planning ahead, no immediate pressure' },
  { value: 'MEDIUM',   label: 'Medium',   desc: 'Need to fill positions within a month' },
  { value: 'HIGH',     label: 'High',     desc: 'Urgent — within 2 weeks' },
  { value: 'CRITICAL', label: 'Critical', desc: 'Need staff immediately' },
];

export default function GenerateStaffingPlan() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedFacilityId = searchParams.get('facilityId');

  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const [form, setForm] = useState({
    facilityId: preselectedFacilityId || '',
    urgencyLevel: 'MEDIUM',
    additionalContext: '',
    requiredInDays: '',
  });

  useEffect(() => { loadFacilities(); }, []);

  const loadFacilities = async () => {
    try {
      const res = await facilityAPI.list();
      const list = res.data.data || [];
      setFacilities(list);
      if (!preselectedFacilityId && list.length === 1) {
        setForm(f => ({ ...f, facilityId: String(list[0].id) }));
      }
    } catch { toast.error('Failed to load facilities'); }
    finally { setLoading(false); }
  };

  const selectedFacility = facilities.find(f => String(f.id) === String(form.facilityId));

  const handleGenerate = async () => {
    if (!form.facilityId) { toast.error('Please select a facility'); return; }
    setGenerating(true);
    try {
      const payload = {
        facilityId: parseInt(form.facilityId),
        urgencyLevel: form.urgencyLevel,
        additionalContext: form.additionalContext || null,
        requiredInDays: form.requiredInDays ? parseInt(form.requiredInDays) : null,
      };
      const res = await staffingPlanAPI.generate(payload);
      const plan = res.data.data;
      toast.success('Staffing plan generated!');
      navigate(`/company/staffing-plans/${plan.id}`);
    } catch (e) {
      const msg = e.response?.data?.message || 'Failed to generate plan';
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const ftypeLabel = (v) => ({
    HOSPITAL: 'Hospital', CLINIC: 'Clinic', HOME: 'Home Care',
    REHAB_CENTER: 'Rehab Center', CORPORATE_OFFICE: 'Corporate/Wellness',
    RESIDENCE: 'Residence',
  }[v] || v);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/company/facilities')}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-7 w-auto" />
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold uppercase">Company</span>
          </div>
          <button onClick={() => { logout(); navigate('/company/login'); }}
            className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-8">

        {/* Hero */}
        <div className="bg-gradient-to-br from-slate-900 to-teal-900 rounded-2xl p-8 mb-8 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at 85% 30%, rgba(20,184,166,0.3) 0%, transparent 60%)' }} />
          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-teal-500/20 rounded-xl flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-teal-400" />
              </div>
              <span className="text-teal-400 text-xs font-bold uppercase tracking-widest">AI-Powered</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Generate Staffing Plan</h1>
            <p className="text-slate-400 text-sm leading-relaxed">
              Tell us about your facility and requirements. ShramSena's AI will generate
              a detailed staffing plan with role recommendations, shift distribution,
              and budget estimates tailored to your city and facility type.
            </p>
          </div>
        </div>

        {/* No facilities warning */}
        {facilities.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-amber-800 text-sm">No facilities added yet</p>
              <p className="text-amber-700 text-sm mt-0.5">
                You need to add at least one facility before generating a staffing plan.
              </p>
              <button onClick={() => navigate('/company/facilities')}
                className="mt-3 flex items-center gap-1.5 bg-amber-600 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-amber-700 transition">
                Add Facility <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}

        {/* Form */}
        {facilities.length > 0 && (
          <div className="space-y-5">

            {/* Step 1: Select facility */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-bold text-slate-800 mb-1">
                <span className="text-teal-600 mr-2">1.</span> Select Facility
              </h2>
              <p className="text-xs text-gray-400 mb-4">
                The AI uses your facility's profile data to tailor the staffing plan.
              </p>

              {facilities.length === 1 ? (
                /* Single facility — auto-selected, show as card */
                <div className="flex items-center gap-4 bg-teal-50 border border-teal-200 rounded-xl p-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center shrink-0">
                    <Building2 className="h-5 w-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-bold text-teal-800">{facilities[0].facilityName}</p>
                    <p className="text-xs text-teal-600">
                      {ftypeLabel(facilities[0].facilityType)}
                      {facilities[0].city ? ` · ${facilities[0].city}` : ''}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {facilities.map(f => (
                    <button key={f.id} type="button"
                      onClick={() => setForm(prev => ({ ...prev, facilityId: String(f.id) }))}
                      className={`w-full text-left flex items-center gap-4 p-4 rounded-xl border-2 transition
                        ${String(form.facilityId) === String(f.id)
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300'}`}>
                      <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center shrink-0">
                        <Building2 className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className={`font-bold text-sm ${String(form.facilityId) === String(f.id) ? 'text-teal-800' : 'text-slate-800'}`}>
                          {f.facilityName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {ftypeLabel(f.facilityType)}
                          {f.city ? ` · ${f.city}` : ''}
                          {f.bedCount ? ` · ${f.bedCount} beds` : ''}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Step 2: Urgency */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-bold text-slate-800 mb-1">
                <span className="text-teal-600 mr-2">2.</span> How Urgent Is This?
              </h2>
              <p className="text-xs text-gray-400 mb-4">
                This helps the AI prioritize which roles to flag as critical.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {URGENCY_OPTIONS.map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => setForm(f => ({ ...f, urgencyLevel: opt.value }))}
                    className={`text-left p-4 rounded-xl border-2 transition
                      ${form.urgencyLevel === opt.value
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300'}`}>
                    <p className={`font-bold text-sm ${form.urgencyLevel === opt.value ? 'text-teal-700' : 'text-slate-700'}`}>
                      {opt.label}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 leading-snug">{opt.desc}</p>
                  </button>
                ))}
              </div>

              {(form.urgencyLevel === 'HIGH' || form.urgencyLevel === 'CRITICAL') && (
                <div className="mt-4">
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">
                    Need staff within how many days?
                  </label>
                  <input type="number" value={form.requiredInDays}
                    onChange={e => setForm(f => ({ ...f, requiredInDays: e.target.value }))}
                    placeholder="e.g. 7" min={1} max={90}
                    className={inp + ' w-32'} />
                </div>
              )}
            </div>

            {/* Step 3: Additional context */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-bold text-slate-800 mb-1">
                <span className="text-teal-600 mr-2">3.</span> Anything Else to Share?
                <span className="text-gray-400 font-normal text-sm ml-1">(Optional)</span>
              </h2>
              <p className="text-xs text-gray-400 mb-4">
                Specific requirements, upcoming events, budget constraints, or context
                that helps the AI give better recommendations.
              </p>
              <textarea value={form.additionalContext}
                onChange={e => setForm(f => ({ ...f, additionalContext: e.target.value }))}
                placeholder="e.g. We are opening a new ICU wing in 3 weeks and need experienced ICU nurses. Budget is tight — looking for cost-effective options."
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" />
            </div>

            {/* Rate limit notice */}
            <div className="flex items-center gap-2 text-xs text-gray-400 px-1">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              You can generate up to 3 staffing plans per day.
            </div>

            {/* Generate button */}
            <button onClick={handleGenerate} disabled={generating || !form.facilityId}
              className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-4 rounded-2xl
                font-bold text-base hover:opacity-90 transition shadow-xl shadow-teal-500/25
                flex items-center justify-center gap-3 disabled:opacity-60">
              {generating ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Generating your staffing plan... this takes 10–20 seconds
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5" />
                  Generate AI Staffing Plan
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
